// ─────────────────────────────────────────────────────────────────────────────
// playerLibrary.js
//
// The curated library of pieces shipped with the Ambient Player MVP.
// Each piece is a Toolkit parameter set + metadata. The Player loads these
// specs via URL params into the Toolkit, which then runs its normal
// rendering and column-scan audio.
//
// IMPORTANT: paletteName must match one of the Toolkit's DEFAULT_PALETTES:
//   classic, grayscale, fire, ocean, rainbow
//
// IMPORTANT: view bounds must have a 3:2 aspect ratio (width / height = 1.5)
// to match the Toolkit's 750×500 canvas. A non-3:2 view will render stretched.
// To convert a 1:1 view at center (cx, cy) with half-height h:
//   xMin = cx - 1.5*h,  xMax = cx + 1.5*h,  yMin = cy - h,  yMax = cy + h
//
// To add a piece: explore in the Toolkit, find a region you like, copy its
// view bounds + paletteName + maxIter, give it metadata, paste here.
// Later this collection migrates to Firestore so adding pieces doesn't
// require a redeploy.
// ─────────────────────────────────────────────────────────────────────────────

export const PLAYER_LIBRARY = [
  {
    id: "slow-wave",
    title: "Slow Wave",
    description: "A drifting current at the edge of stillness.",
    mood: "calming",
    classTypes: ["yin", "meditation", "restorative"],
    suggestedDurations: [20, 45, 60],
    preview: true,
    spec: {
      fractalType: "mandelbrot",
      view: { xMin: -0.25, xMax: 0.05, yMin: -0.95, yMax: -0.75 },
      maxIter: 600,
      juliaC: { re: 0, im: 0 },
      paletteName: "ocean",
    },
  },
  {
    id: "morning-mist",
    title: "Morning Mist",
    description: "Light awakening through layered translucence.",
    mood: "energizing",
    classTypes: ["vinyasa", "breathwork"],
    suggestedDurations: [20, 45],
    preview: true,
    spec: {
      fractalType: "mandelbrot",
      view: { xMin: -0.7875, xMax: -0.7125, yMin: 0.10, yMax: 0.15 },
      maxIter: 800,
      juliaC: { re: 0, im: 0 },
      paletteName: "classic",
    },
  },
  {
    id: "deep-current",
    title: "Deep Current",
    description: "Something vast and unhurried beneath the surface.",
    mood: "grounding",
    classTypes: ["meditation", "breathwork", "restorative"],
    suggestedDurations: [45, 60, 90],
    preview: true,
    spec: {
      fractalType: "mandelbrot",
      view: { xMin: -1.41, xMax: -1.35, yMin: -0.02, yMax: 0.02 },
      maxIter: 1200,
      juliaC: { re: 0, im: 0 },
      paletteName: "ocean",
    },
  },
  {
    id: "sea-garden",
    title: "Sea Garden",
    description: "Spirals opening, holding, dissolving.",
    mood: "expansive",
    classTypes: ["sound-bath", "savasana", "meditation"],
    suggestedDurations: [45, 60, 90],
    preview: false,
    spec: {
      fractalType: "mandelbrot",
      view: { xMin: -0.75625, xMax: -0.73375, yMin: 0.09, yMax: 0.105 },
      maxIter: 1500,
      juliaC: { re: 0, im: 0 },
      paletteName: "rainbow",
    },
  },
  {
    id: "embers",
    title: "Embers",
    description: "Warmth held against the dark, slowly turning.",
    mood: "calming",
    classTypes: ["yin", "meditation", "restorative"],
    suggestedDurations: [45, 60, 90],
    preview: false,
    spec: {
      fractalType: "mandelbrot",
      view: { xMin: -1.795, xMax: -1.705, yMin: -0.03, yMax: 0.03 },
      maxIter: 1000,
      juliaC: { re: 0, im: 0 },
      paletteName: "fire",
    },
  },
];


// ─── Filter options exposed to the operator UI ──────────────────────────────

export const MOOD_OPTIONS = [
  { value: "all",        label: "All moods"  },
  { value: "calming",    label: "Calming"    },
  { value: "energizing", label: "Energizing" },
  { value: "grounding",  label: "Grounding"  },
  { value: "expansive",  label: "Expansive"  },
];

export const CLASS_TYPE_OPTIONS = [
  { value: "all",         label: "All class types" },
  { value: "yin",         label: "Yin"             },
  { value: "vinyasa",     label: "Vinyasa"         },
  { value: "meditation",  label: "Meditation"      },
  { value: "sound-bath",  label: "Sound bath"      },
  { value: "breathwork",  label: "Breathwork"      },
  { value: "restorative", label: "Restorative"     },
  { value: "savasana",    label: "Savasana"        },
];

export const DURATION_OPTIONS = [
  { value: 0,  label: "Any duration"   },
  { value: 20, label: "20 minutes"     },
  { value: 45, label: "45 minutes"     },
  { value: 60, label: "60 minutes"     },
  { value: 90, label: "90 minutes"     },
];


// ─── Filtering and lookup helpers ───────────────────────────────────────────

export function filterLibrary(pieces, { mood, classType, duration }) {
  return pieces.filter((p) => {
    if (mood && mood !== "all" && p.mood !== mood) return false;
    if (classType && classType !== "all" && !p.classTypes.includes(classType)) return false;
    if (duration && duration !== 0 && !p.suggestedDurations.includes(duration)) return false;
    return true;
  });
}

export function getPieceById(id) {
  return PLAYER_LIBRARY.find((p) => p.id === id) || null;
}
